import React from 'react';
import { Container, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'react-bootstrap-icons';
import LandingHeader from '../components/LandingHeader';
import LandingFooter from '../components/LandingFooter';
import useScrollToTop from '../hooks/useScrollToTop';
import whoDesigners from '../assets/who/who-designers.png';
import whoAgencies from '../assets/who/who-agencies.png';
import whoStudents from '../assets/who/who-students.png';
import whoProductManagers from '../assets/who/who-productmanagers.png';
import whoMomentumSeekers from '../assets/who/who-momentumseekers.png';
import './WhoIsThisFor.css';

const WhoIsThisFor: React.FC = () => {
  const navigate = useNavigate();
  useScrollToTop();

  const handleTryNow = () => {
    navigate('/register');
  };

  return (
    <div className="who-page landing-page">
      <LandingHeader />

      <Container>
        <div className="page-header">
          <h1 className="page-title">Who Is Revealer AI For?</h1>
          <p className="page-subtitle">
            Revealer AI is built for the ones in motion. It is the co-creative ecosystem for anyone who has ever felt the weight of a blank screen or the paralysis of scattered research. We bridge the gap between "stuck" and "started," turning the chaos of early discovery into meaningful momentum.
          </p>
        </div>

        <div className="audience-sections">
          <div className="audience-section">
            <div className="section-image">
              <img src={whoDesigners} alt="Professional UX & Product Designers" />
            </div>
            <div className="section-content">
              <h3>Professional UX & Product Designers</h3>
              <p>
                You're tired of the manual grind of tagging endless transcripts and manually drafting the same early-stage flows. You don't need a tool to replace your thinking; you need one to sharpen it.
              </p>
              <p className="use-revealer">
                <strong>Use Revealer to:</strong> Instantly transform your raw research into <strong>Wireframes</strong>, <strong>User Flows</strong>, and <strong>Sitemaps</strong>, giving you a solid foundation to react to and refine.
              </p>
            </div>
          </div>

          <div className="audience-section">
            <div className="section-image">
              <img src={whoAgencies} alt="Design & Product Agencies" />
            </div>
            <div className="section-content">
              <h3>Design & Product Agencies</h3>
              <p>
                In the agency world, speed is a superpower, but clarity is the currency. Use Revealer to compress the discovery phase without sacrificing the depth your clients expect.
              </p>
              <p className="use-revealer">
                <strong>Use Revealer to:</strong> Conduct rapid <strong>Competitive Analysis</strong>, generate <strong>Research Synthesis</strong> reports, and create <strong>Personas</strong> grounded in actual client data, not generic templates.
              </p>
            </div>
          </div>

          <div className="audience-section">
            <div className="section-image">
              <img src={whoStudents} alt="UX & Design Students" />
            </div>
            <div className="section-content">
              <h3>UX & Design Students</h3>
              <p>
                Learning the discovery process shouldn't feel like drowning in data. Revealer acts as a guide at your side, helping you see the patterns in the noise as you build your craft.
              </p>
              <p className="use-revealer">
                <strong>Use Revealer to:</strong> Structure your early thinking with <strong>Ideation</strong> prompts, ensure your projects meet professional standards with <strong>Accessibility Compliance</strong> checks, and learn how to bridge the gap from research to design.
              </p>
            </div>
          </div>

          <div className="audience-section">
            <div className="section-image">
              <img src={whoProductManagers} alt="Product Managers" />
            </div>
            <div className="section-content">
              <h3>Product Managers</h3>
              <p>
                You have the vision, but aligning the team around the right insights can be the biggest bottleneck. Revealer helps you turn a mountain of stakeholder feedback and user interviews into a clear, actionable path forward.
              </p>
              <p className="use-revealer">
                <strong>Use Revealer to: Summarize Meetings</strong> and <strong>Transcripts</strong> into key takeaways, and build a <strong>UX Roadmap</strong> that keeps the team moving toward the same goal.
              </p>
            </div>
          </div>

          <div className="audience-section">
            <div className="section-image">
              <img src={whoMomentumSeekers} alt="The Momentum Seekers" />
            </div>
            <div className="section-content">
              <h3>The Momentum Seekers</h3>
              <p>
                If you are someone who seeks an <strong>intelligent discovery-to-early-design accelerator</strong>, Revealer was made for you. Whether you are a solo founder or a developer, if you have raw inputs and need real direction, you belong here.
              </p>
              <p className="use-revealer">
                <strong>Use Revealer to:</strong> Get objective feedback through <strong>Design Reviews</strong>, organize complex ideas with <strong>Interaction Design</strong> suggestions, and facilitate better team alignment with AI-generated <strong>Workshops</strong>.
              </p>
            </div>
          </div>
        </div>

        <p className="page-tagline">
          Momentum doesn't come from waiting for genius to strike. It comes from simply starting.
        </p>
      </Container>

      <section className="cta-section">
        <Container>
          <h2>Join The Ones In Motion.</h2>
          <p>Get started with your first project.</p>
          <Button variant="primary" onClick={handleTryNow}>
            TRY REVEALER NOW <ArrowUpRight className="ms-2" />
          </Button>
        </Container>
      </section>

      <LandingFooter />
    </div>
  );
};

export default WhoIsThisFor;
